import React, { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  ExternalLink,
  Loader2,
  MessageSquare,
  RefreshCcw,
  Send,
  Tag as TagIcon,
} from "lucide-react";
import { toast } from "sonner";
import { api, thumbUrl } from "@/lib/api";
import type {
  Comment,
  MediaAsset,
  PlatformAccount,
  Product,
  PublishJob,
} from "@/lib/types";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { EmptyState } from "@/components/shared/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { IntentChip, PlatformChip, PublishChip } from "@/components/shared/Chips";
import { ProductScoreGauge } from "@/components/shared/ProductScoreGauge";

export const ProductDetail: React.FC = () => {
  const { productId } = useParams<{ productId: string }>();
  const qc = useQueryClient();
  const nav = useNavigate();
  const [publishOpen, setPublishOpen] = useState(false);
  const [thumbOpen, setThumbOpen] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState<MediaAsset | null>(null);
  const [caption, setCaption] = useState("");
  const [hashtags, setHashtags] = useState("");
  const [selectedAccounts, setSelectedAccounts] = useState<string[]>([]);
  const [publishMediaId, setPublishMediaId] = useState<string | null>(null);
  const [publishing, setPublishing] = useState(false);

  const product = useQuery<Product>({
    queryKey: ["product", productId],
    queryFn: async () => (await api.get(`/products/${productId}`)).data,
    enabled: !!productId,
  });
  const media = useQuery<MediaAsset[]>({
    queryKey: ["product-media", productId],
    queryFn: async () => (await api.get(`/media?product_id=${productId}`)).data,
    enabled: !!productId,
  });
  const accounts = useQuery<PlatformAccount[]>({
    queryKey: ["platforms"],
    queryFn: async () => (await api.get("/platforms")).data,
  });
  const comments = useQuery<Comment[]>({
    queryKey: ["product-comments", productId],
    queryFn: async () => (await api.get(`/comments?product_id=${productId}`)).data,
    enabled: !!productId,
  });
  const jobs = useQuery<PublishJob[]>({
    queryKey: ["product-jobs", productId],
    queryFn: async () => (await api.get("/publish")).data,
  });

  const productJobs = (jobs.data || []).filter((j) => j.product_id === productId);

  const regenerate = useMutation({
    mutationFn: async (mediaId: string) =>
      (await api.post(`/media/${mediaId}/regenerate-thumbnails`)).data,
    onSuccess: () => {
      toast.success("Thumbnails regenerated");
      qc.invalidateQueries({ queryKey: ["product-media", productId] });
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.detail || "Regeneration failed"),
  });

  const setDefaultThumb = useMutation({
    mutationFn: async ({ mediaId, thumbId }: { mediaId: string; thumbId: string }) =>
      (await api.post(`/media/${mediaId}/set-default-thumb/${thumbId}`)).data,
    onSuccess: () => {
      toast.success("Default thumbnail updated");
      qc.invalidateQueries({ queryKey: ["product-media", productId] });
    },
  });

  const openPublish = (m: MediaAsset) => {
    setPublishMediaId(m.media_id);
    setPublishOpen(true);
  };

  const openThumbs = (m: MediaAsset) => {
    setSelectedMedia(m);
    setThumbOpen(true);
  };

  const handlePublish = async () => {
    if (!productId) return;
    if (selectedAccounts.length === 0) {
      toast.error("Select at least one platform account");
      return;
    }
    if (!caption.trim()) {
      toast.error("Caption is required");
      return;
    }
    setPublishing(true);
    try {
      const payload = {
        product_id: productId,
        media_id: publishMediaId,
        caption: caption.trim(),
        hashtags: hashtags.split(",").map((t) => t.trim()).filter(Boolean),
        account_ids: selectedAccounts,
      };
      await api.post("/publish", payload);
      toast.success("Publish job queued. Watch it in Posts.");
      setPublishOpen(false);
      setCaption("");
      setHashtags("");
      setSelectedAccounts([]);
      qc.invalidateQueries({ queryKey: ["product-jobs", productId] });
      setTimeout(() => {
        qc.invalidateQueries({ queryKey: ["product-jobs", productId] });
        qc.invalidateQueries({ queryKey: ["product-comments", productId] });
      }, 2500);
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Publish failed");
    } finally {
      setPublishing(false);
    }
  };

  if (product.isLoading) {
    return (
      <DashboardLayout title="Loading…">
        <div className="sg-skeleton h-80" />
      </DashboardLayout>
    );
  }

  const p = product.data;
  if (!p) {
    return (
      <DashboardLayout title="Not found">
        <EmptyState title="Product not found" description="It may have been deleted." />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title={p.title} subtitle={p.description || undefined} seoPath={`/products/${p.product_id}`} seoDescription={p.description || `${p.title} — SociBaba product workspace.`}
      actions={
        <Link to={`/products/${p.product_id}/edit`}>
          <Button variant="outline" size="sm" data-testid="edit-product-btn">Edit product</Button>
        </Link>
      }
    >
      <Link to="/products" className="text-sm text-[color:var(--sg-muted)] hover:text-[color:var(--sg-fg)] inline-flex items-center gap-1 mb-4">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to products
      </Link>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <div className="sg-card p-6">
            <div className="flex items-start justify-between gap-6 flex-wrap">
              <div className="flex-1">
                <div className="sg-label mb-4">Details</div>
                <dl className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              <div>
                <dt className="text-xs text-[color:var(--sg-muted)] uppercase tracking-wider mb-1">Category</dt>
                <dd className="font-medium">{p.category || "—"}</dd>
              </div>
              <div>
                <dt className="text-xs text-[color:var(--sg-muted)] uppercase tracking-wider mb-1">Price</dt>
                <dd className="font-medium">{p.price ? `₹${p.price.toLocaleString()}` : "—"}</dd>
              </div>
              <div>
                <dt className="text-xs text-[color:var(--sg-muted)] uppercase tracking-wider mb-1">Support</dt>
                <dd className="font-medium truncate">{p.support_contact || "—"}</dd>
              </div>
              <div className="md:col-span-3">
                <dt className="text-xs text-[color:var(--sg-muted)] uppercase tracking-wider mb-1">Product link</dt>
                <dd>
                  {p.product_link ? (
                    <a href={p.product_link} target="_blank" rel="noreferrer" className="text-[color:var(--sg-primary)] hover:underline inline-flex items-center gap-1">
                      {p.product_link} <ExternalLink className="h-3 w-3" />
                    </a>
                  ) : (
                    "—"
                  )}
                </dd>
              </div>
              {p.tags?.length > 0 && (
                <div className="md:col-span-3">
                  <dt className="text-xs text-[color:var(--sg-muted)] uppercase tracking-wider mb-2">Tags</dt>
                  <dd className="flex flex-wrap gap-1.5">
                    {p.tags.map((t) => (
                      <span key={t} className="sg-chip bg-[color:var(--sg-muted-bg)] border-[color:var(--sg-border)]">
                        <TagIcon className="h-3 w-3" /> {t}
                      </span>
                    ))}
                  </dd>
                </div>
              )}
            </dl>
              </div>
              <ProductScoreCard product={p} />
            </div>
          </div>

          <div className="sg-card p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="sg-label">Media</div>
                <div className="font-display text-lg font-semibold mt-1">Attachments</div>
              </div>
              <Link to={`/upload?product_id=${p.product_id}`}>
                <Button variant="outline" size="sm">Upload new</Button>
              </Link>
            </div>
            {media.data && media.data.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {media.data.map((m) => {
                  const def = m.thumbnails.find((t) => t.is_default) ?? m.thumbnails[0];
                  return (
                    <div key={m.media_id} className="sg-card overflow-hidden group" data-testid={`media-card-${m.media_id}`}>
                      <div className="aspect-video bg-[color:var(--sg-muted-bg)] relative">
                        {def ? (
                          <img
                            src={thumbUrl(m.media_id, def.thumb_id)}
                            alt={m.original_filename}
                            loading="lazy"
                            decoding="async"
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <div className="h-full w-full flex items-center justify-center text-xs text-[color:var(--sg-muted)]">
                            {m.status}
                          </div>
                        )}
                        <div className="absolute top-2 left-2">
                          <span className="sg-chip bg-white/90 backdrop-blur">{m.kind}</span>
                        </div>
                      </div>
                      <div className="p-3 space-y-2">
                        <div className="text-xs font-medium truncate">{m.original_filename}</div>
                        <div className="flex gap-1.5">
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-xs h-7"
                            onClick={() => openThumbs(m)}
                            disabled={m.raw_deleted && m.thumbnails.length === 0}
                            data-testid={`thumbs-btn-${m.media_id}`}
                          >
                            <RefreshCcw className="h-3 w-3 mr-1" /> Thumbnails
                          </Button>
                          <Button
                            size="sm"
                            className="text-xs h-7 bg-[color:var(--sg-fg)] text-white hover:bg-black hover:text-white"
                            onClick={() => openPublish(m)}
                            data-testid={`publish-btn-${m.media_id}`}
                          >
                            <Send className="h-3 w-3 mr-1" /> Publish
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <EmptyState
                title="No media yet"
                description="Upload an image or video to publish across platforms."
                action={
                  <Link to={`/upload?product_id=${p.product_id}`}>
                    <Button className="bg-[color:var(--sg-fg)] text-white hover:bg-black hover:text-white">
                      Upload media
                    </Button>
                  </Link>
                }
              />
            )}
          </div>

          <div className="sg-card p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="sg-label">Comments</div>
                <div className="font-display text-lg font-semibold mt-1">Audience signals</div>
              </div>
              <Link to={`/comments?product_id=${p.product_id}`}>
                <Button variant="ghost" size="sm">Open inbox</Button>
              </Link>
            </div>
            {comments.data && comments.data.length > 0 ? (
              <ul className="space-y-3">
                {comments.data.slice(0, 6).map((c) => (
                  <li key={c.comment_id} className="pb-3 border-b border-[color:var(--sg-border)] last:border-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className="text-sm font-semibold">{c.author_handle}</span>
                      <PlatformChip platform={c.platform} />
                      <IntentChip intent={c.intent} />
                    </div>
                    <p className="text-sm text-[color:var(--sg-fg)]">{c.text}</p>
                  </li>
                ))}
              </ul>
            ) : (
              <EmptyState title="No comments yet" description="Publish this product to start collecting signals." />
            )}
          </div>
        </div>

        <div className="space-y-4">
          <div className="sg-card p-6">
            <div className="sg-label mb-3">Publish jobs</div>
            {productJobs.length > 0 ? (
              <div className="space-y-3">
                {productJobs.slice(0, 8).map((j) => (
                  <div key={j.job_id} className="pb-3 border-b border-[color:var(--sg-border)] last:border-0">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <div className="text-xs text-[color:var(--sg-muted)]">
                        {new Date(j.created_at).toLocaleString()}
                      </div>
                      <PublishChip status={j.overall_status} />
                    </div>
                    <div className="text-sm truncate">{j.caption}</div>
                    <div className="flex gap-1 mt-2 flex-wrap">
                      {j.targets.map((t) => (
                        <PlatformChip key={t.target_id} platform={t.platform} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-[color:var(--sg-muted)]">
                No publish jobs yet for this product.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Publish dialog */}
      <Dialog open={publishOpen} onOpenChange={setPublishOpen}>
        <DialogContent className="max-w-lg bg-white">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Publish content</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider">Caption</Label>
              <Textarea
                data-testid="publish-caption"
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                rows={3}
                placeholder={`Introducing ${p.title}…`}
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Hashtags (comma separated)</Label>
              <Input
                data-testid="publish-hashtags"
                value={hashtags}
                onChange={(e) => setHashtags(e.target.value)}
                placeholder="new, launch, summer"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider mb-2 block">Target accounts</Label>
              {accounts.data && accounts.data.length > 0 ? (
                <div className="space-y-2">
                  {accounts.data.map((a) => (
                    <label
                      key={a.account_id}
                      className="flex items-center gap-3 p-2 rounded border border-[color:var(--sg-border)] cursor-pointer hover:bg-[color:var(--sg-muted-bg)]"
                    >
                      <Checkbox
                        data-testid={`publish-account-${a.account_id}`}
                        checked={selectedAccounts.includes(a.account_id)}
                        onCheckedChange={(v) => {
                          if (v) setSelectedAccounts([...selectedAccounts, a.account_id]);
                          else setSelectedAccounts(selectedAccounts.filter((x) => x !== a.account_id));
                        }}
                      />
                      <div className="flex-1">
                        <div className="text-sm font-medium">{a.display_name}</div>
                        <div className="text-xs text-[color:var(--sg-muted)]">
                          {a.platform} · {a.handle}
                        </div>
                      </div>
                      <PlatformChip platform={a.platform} />
                    </label>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-[color:var(--sg-muted)]">
                  No platforms connected.{" "}
                  <Link to="/platforms" className="text-[color:var(--sg-primary)] hover:underline">
                    Connect one
                  </Link>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPublishOpen(false)}>Cancel</Button>
            <Button
              data-testid="publish-confirm"
              onClick={handlePublish}
              disabled={publishing}
              className="bg-[color:var(--sg-fg)] text-white hover:bg-black hover:text-white"
            >
              {publishing ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                <><Send className="h-3.5 w-3.5 mr-1.5" /> Publish now</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Thumbnail picker */}
      <Dialog open={thumbOpen} onOpenChange={setThumbOpen}>
        <DialogContent className="max-w-2xl bg-white">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Thumbnails</DialogTitle>
          </DialogHeader>
          {selectedMedia && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {selectedMedia.thumbnails.map((t) => (
                  <button
                    key={t.thumb_id}
                    data-testid={`thumb-${t.thumb_id}`}
                    onClick={() =>
                      setDefaultThumb.mutate({
                        mediaId: selectedMedia.media_id,
                        thumbId: t.thumb_id,
                      })
                    }
                    className={`relative rounded-md overflow-hidden border-2 transition-all ${
                      t.is_default
                        ? "border-[color:var(--sg-primary)]"
                        : "border-[color:var(--sg-border)] hover:border-[color:var(--sg-fg)]"
                    }`}
                  >
                    <img
                      src={thumbUrl(selectedMedia.media_id, t.thumb_id)}
                      alt={`Thumb ${t.thumb_id}`}
                      loading="lazy"
                      decoding="async"
                      className="w-full aspect-video object-cover"
                    />
                    {t.is_default && (
                      <span className="absolute top-1 left-1 sg-chip bg-[color:var(--sg-primary)] text-white border-transparent">
                        Default
                      </span>
                    )}
                  </button>
                ))}
              </div>
              <div className="flex justify-end">
                <Button
                  variant="outline"
                  disabled={selectedMedia.raw_deleted || regenerate.isPending}
                  onClick={() => regenerate.mutate(selectedMedia.media_id)}
                  data-testid="regen-thumbs-btn"
                >
                  {regenerate.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                  ) : (
                    <RefreshCcw className="h-4 w-4 mr-1.5" />
                  )}
                  Regenerate thumbnails
                </Button>
              </div>
              {selectedMedia.raw_deleted && (
                <div className="text-xs text-[color:var(--sg-muted)]">
                  Raw media has been auto-deleted after successful publishing. Existing thumbnails remain, but new ones cannot be generated.
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

// ------------------------------------------------------------------
// ProductScoreCard — hero gauge + breakdown popover trigger
// ------------------------------------------------------------------
const ProductScoreCard: React.FC<{ product: Product }> = ({ product }) => {
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();
  const recompute = useMutation({
    mutationFn: async () =>
      (await api.post(`/products/${product.product_id}/recompute-score`)).data,
    onSuccess: () => {
      toast.success("Score refreshed");
      qc.invalidateQueries({ queryKey: ["product", product.product_id] });
      qc.invalidateQueries({ queryKey: ["products"] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Recompute failed"),
  });
  return (
    <div className="shrink-0" data-testid="product-score-card">
      <ProductScoreGauge
        score={product.product_score ?? null}
        band={product.score_band || undefined}
        size="md"
        showLabel
      />
      <div className="flex items-center gap-2 mt-2 justify-center">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setOpen(true)}
          data-testid="product-score-why-btn"
          className="text-xs"
        >
          Why this score?
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => recompute.mutate()}
          disabled={recompute.isPending}
          data-testid="product-score-recompute-btn"
          className="text-xs"
        >
          {recompute.isPending ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <RefreshCcw className="h-3 w-3" />
          )}
        </Button>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">
              SIBA Score · {product.product_score ?? "—"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-1">
            {(product.score_breakdown || []).length === 0 ? (
              <div className="text-sm text-[color:var(--sg-muted)]">
                Score hasn't been computed yet. Hit refresh.
              </div>
            ) : (
              (product.score_breakdown || []).map((b) => (
                <div
                  key={b.signal}
                  className="flex items-center justify-between px-3 py-2 rounded-md hover:bg-[color:var(--sg-muted-bg)]/60"
                  data-testid={`score-signal-${b.signal}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-[color:var(--sg-fg)]">{b.label}</div>
                    <div className="text-[10px] text-[color:var(--sg-muted)] mt-0.5">{b.note}</div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <div className="w-24 h-1.5 rounded-full bg-[color:var(--sg-muted-bg)] overflow-hidden">
                      <div
                        className="h-full bg-[color:var(--sb-saffron)]"
                        style={{ width: `${Math.round((b.raw ?? 0) * 100)}%` }}
                      />
                    </div>
                    <div className="text-xs font-mono text-[color:var(--sg-muted)] w-16 text-right">
                      +{Math.round(b.contribution)}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
          <div className="text-[10px] text-[color:var(--sg-muted)] pt-3 border-t border-[color:var(--sg-border)] mt-3">
            Signals refresh automatically whenever the product is edited, a new
            comment/lead/complaint arrives, or you hit the refresh button.
            300-549 needs improvement · 550-649 emerging · 650-749 solid · 750-900 trusted.
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

